import { useCallback } from 'react';
import { Edge, Node, Position, useReactFlow } from 'reactflow';

// @ts-ignore flextree isn't typed
import { flextree } from 'd3-flextree';
import { HierarchyNode, stratify } from 'd3-hierarchy';

import { useSidebarState } from '@components/sidebar';
import { remToPx } from '@utils/converters';

import { JourneyActionValuesTypes } from 'api/types';
import { conditionGapSize } from 'ui/actions/condition-v2/helpers';
import { getNodeHeight } from 'ui/journey-builder/hooks/use-nodes';

import { TravellerJourneyNode } from '../node-types/types';
const BADGE_HEIGHT_MD = 24;

type LoopNode = {
  child: string;
  parent: string;
  nodeDescription: string;
  description?: string;
};

const layoutWidth = 352;

const calculateNodeSize = (node: HierarchyNode<Node>): [number, number] => {
  if (node.data.type === 'journey_branch') {
    return [layoutWidth, 140];
  } else if (node.data.type === 'journey_merge') {
    return [layoutWidth, 90];
  } else if (node.data.type === 'journey_loop') {
    return [layoutWidth, 140];
  } else if (node.data.type === 'branch_base') {
    return [layoutWidth, 72];
  }

  // node.data is the reactflow Node object with its own .data property
  const height = getNodeHeight(node.data as Node);

  let extraHeight = 24;
  if (node.data.type === 'journey_triggers') {
    extraHeight += 72;
  }
  if (
    (node.data.type === 'condition' || node.data.type === 'condition_v2') &&
    ((node.data.data.isHalted && node.data.data.isCompleted) || node.data.data.result?.isBypassed)
  ) {
    extraHeight += BADGE_HEIGHT_MD + remToPx(conditionGapSize);
  }
  if (node.data.type === 'journey_start') {
    extraHeight += -14;
  }
  if (node.data.type === 'break_loop') {
    extraHeight += 24;
  }
  if (node.data.type === 'ghost') {
    return [100, height + 24];
  }
  if (node.data.data.isSkipped) {
    extraHeight += 48;
  }
  return [layoutWidth, height + extraHeight];
};

const mapNodePositions = (nodes: Node[]) => {
  // stratify() throws "no root" on an empty list. A traveller journey has no
  // actions until celery instantiates them, so opening it right after a form
  // submission must render an empty canvas, not crash the route.
  if (nodes.length === 0) {
    return [];
  }

  const sortChildren = (a: HierarchyNode<Node>, b: HierarchyNode<Node>) => {
    if (b.data.type === 'ghost') {
      return 1;
    }
    if (a.data.type === 'ghost') {
      return -1;
    }
    const order: string[] = a.parent?.data?.data?.values?.order ?? [];
    return order.indexOf(a.id ?? '') - order.indexOf(b.id ?? '');
  };
  const tree = stratify<Node>()(nodes).sort(sortChildren);

  // Batch measure all nodes at once (single reflow)
  const nodeSizeCache = new Map<string, [number, number]>();
  tree.each((node: HierarchyNode<Node>) => {
    const size = calculateNodeSize(node);
    nodeSizeCache.set(node.id ?? '', size);
  });

  // Create layout with lookup function (no DOM access during layout)
  const cachedLayout = flextree<Node>().nodeSize((node: Node) => {
    return nodeSizeCache.get(node.id ?? '') || [layoutWidth, 100];
  });

  const root = cachedLayout(tree);

  return nodes.map((node) => {
    // find the node in the tree with the same id and get its coordinates
    const { x, y } = root.find((d: Node) => d.id === node.id) || node.position;
    const adjustedX = node.type === 'ghost' ? x + 175 : x;
    const parentNode = root.find((d: Node) => d.id === node.parentId);
    const parentX = parentNode ? parentNode.x : 0;
    const parentY = parentNode ? parentNode.y : 0;

    return {
      ...node,
      sourcePosition: node.sourcePosition,
      targetPosition: node.targetPosition,
      position: { x: adjustedX - parentX, y: y - parentY },
      width: 320,
    };
  });
};

export const useNodes = () => {
  const { getNodes, setNodes: setFlowNodes, setEdges } = useReactFlow();
  const { activeId } = useSidebarState();
  const selectedNode = getNodes().find((node) => node.id === activeId) as TravellerJourneyNode;

  const setNodes = useCallback(
    (nodes: TravellerJourneyNode[]) => {
      const newEdges: Edge[] = [];

      const loopGhostNodes: TravellerJourneyNode[] = [];
      const createLoopGhostNodes = (loopActionId: string, breakLoopNode: TravellerJourneyNode) => {
        const loopNode = nodes.find((n) => n.id === loopActionId);
        const loopInProgress =
          breakLoopNode.data.status !== 'completed' && loopNode?.data.status === 'in_progress';
        let currentNode = breakLoopNode;
        const hierarchy: LoopNode[] = [];
        while (currentNode) {
          const ghostId = `${currentNode.id}-ghost`;
          const parentId = `${currentNode.parentId}${
            currentNode.parentId === loopActionId ? '' : '-ghost'
          }`;
          if (currentNode.id === loopActionId) {
            break;
          }
          hierarchy.push({
            child: ghostId,
            parent: parentId,
            nodeDescription: currentNode.data.nodeDescription,
            description: currentNode.data.description,
          });
          const currentParentId = currentNode.parentId;
          currentNode = nodes.find((n) => n.id === currentParentId) as TravellerJourneyNode;
        }
        hierarchy.forEach(({ child, parent, description, nodeDescription }) => {
          loopGhostNodes.push({
            id: child,
            type: 'ghost',
            width: 1,
            height: 1,
            targetPosition: parent === loopActionId ? Position.Top : Position.Bottom,
            sourcePosition: parent === loopActionId ? Position.Top : Position.Bottom,
            parentId: parent,
            data: {
              nodeDescription,
              description,
              isJourneyPaused: false,
              journeyId: '',
              travellerJourneyId: '',
              dependants: [],
              groupActions: [],
            },
          });
          newEdges.push({
            id: `e-${child}`,
            source: parent,
            target: child,
            type: loopInProgress ? 'loopEdgeInProgress' : 'loopEdge',
          });
        });
        const lastNodes = hierarchy
          .filter((node) => !hierarchy.some((item) => item.parent === node.child))
          .map((node) => node.child);
        lastNodes.forEach((lastNode) => {
          newEdges.push({
            id: `e-${lastNode}-break-loop`,
            source: breakLoopNode.id,
            target: lastNode,
            type: loopInProgress ? 'loopEdgeInProgress' : 'loopEdge',
            zIndex: -1,
          });
        });
      };

      nodes.forEach((node) => {
        if (node.type === 'journey_start') {
          return;
        }
        const source = node.parentId ?? 'journey_start';
        const target = node.id;
        const isSucceedEdge =
          node.data.parentCompleted || (node.data.hasFlowReached && node.data.parentSkipped);
        const edge = {
          id: `e-${source}-${target}`,
          source,
          target,
          ...(isSucceedEdge && {
            type: 'travellerSucceedEdge',
          }),
        };
        newEdges.push(edge);
        if (node.type === 'break_loop') {
          const data = node.data.values as JourneyActionValuesTypes['break_loop'];
          if (data?.loopAction) {
            createLoopGhostNodes(data.loopAction, node);
          }
        }
        if (node.data.dependants) {
          node.data.dependants.forEach((dependant) => {
            const additionalEdge = {
              id: `e-${dependant}-${target}`,
              source: dependant,
              target,
              animated: true,
              type: 'travellerExtraEdge',
            };
            newEdges.push(additionalEdge);
          });
        }
      });
      setEdges(newEdges);
      const newNodes = mapNodePositions([...nodes, ...loopGhostNodes] as Node[]);
      setFlowNodes(newNodes);
    },
    [setEdges, setFlowNodes]
  );

  return {
    setNodes,
    selectedNode,
    getNodes,
  };
};
